import {
  CHECKOUT_SESSION_MIN_RESUME_MINUTES,
  CHECKOUT_SESSION_STORAGE_KEY,
  CHECKOUT_SESSION_TIMEOUT_MINUTES,
} from './checkoutSession.constants';
import {
  TCheckoutSession,
  TCheckoutSessionData,
  TCheckoutSessionStep,
} from './checkoutSession.types';

export const loadCheckoutSessionFromStorage = (): TCheckoutSession | null => {
  try {
    const rawValue = localStorage.getItem(CHECKOUT_SESSION_STORAGE_KEY);
    if (!rawValue) {
      return null;
    }
    return JSON.parse(rawValue) as TCheckoutSession;
  } catch (error) {
    console.error('Error loading checkout session from localStorage:', error);
    return null;
  }
};

export const saveCheckoutSessionToStorage = (
  session: TCheckoutSession | null
): void => {
  try {
    if (!session) {
      localStorage.removeItem(CHECKOUT_SESSION_STORAGE_KEY);
      return;
    }
    localStorage.setItem(CHECKOUT_SESSION_STORAGE_KEY, JSON.stringify(session));
  } catch (error) {
    console.error('Error saving checkout session to localStorage:', error);
  }
};

export const clearCheckoutSessionFromStorage = (): void => {
  try {
    localStorage.removeItem(CHECKOUT_SESSION_STORAGE_KEY);
  } catch (error) {
    console.error('Error clearing checkout session from localStorage:', error);
  }
};

export const createCheckoutSession = (
  checkoutData: TCheckoutSessionData,
  timeoutMinutes = CHECKOUT_SESSION_TIMEOUT_MINUTES
): TCheckoutSession => {
  const now = Date.now();
  const startDate = new Date(now).toISOString();
  const abortDate = new Date(now + timeoutMinutes * 60_000).toISOString();

  return {
    checkoutData,
    checkoutStartDate: startDate,
    checkoutAbortDate: abortDate,
  };
};

export const getCheckoutSessionRemainingMs = (
  session: TCheckoutSession | null,
  nowMs = Date.now()
): number => {
  if (!session) return 0;
  return new Date(session.checkoutAbortDate).getTime() - nowMs;
};

export const isCheckoutSessionExpired = (
  session: TCheckoutSession | null,
  nowMs = Date.now()
): boolean => {
  return getCheckoutSessionRemainingMs(session, nowMs) <= 0;
};

export const canShowCheckoutInterruptedToast = (
  session: TCheckoutSession
): boolean => {
  const remainingMs = getCheckoutSessionRemainingMs(session);
  const currentStep = session.checkoutData.currentStep;
  const requiredMinutes = CHECKOUT_SESSION_MIN_RESUME_MINUTES[currentStep] ?? 0;

  return remainingMs >= requiredMinutes * 60_000;
};

export const sanitizeCheckoutSessionStep = (
  step: string
): TCheckoutSessionStep => {
  if (step === 'payment') return 'payment';
  return 'shipping';
};
